/* eslint-disable max-lines */
import React, { useEffect, useMemo } from 'react';
import * as Sentry from '@sentry/react';
// import { PulsarMenuBar } from '@pulsarml/avalon-ui';
import {
  AlarmClock,
  ArrowRightFromLine,
  BarChart4,
  Box,
  CircleAlert,
  CircleHelp,
  CircleUserRound,
  ClipboardList,
  Headphones,
  LayoutDashboard,
  LogOut,
  Settings
} from 'lucide-react';
import { I18n, Translate } from '@utils/i18n';
import {
  useFeatureFlags,
  useFeatureFlagsAnyEnabled,
  useShowProduction
} from '@hooks/useFeatureFlags';

import { Auth } from 'aws-amplify';
import PulsarMenuBar, { MenuItem } from '../../../components/PulsarMenuBar';
import { sessionService } from 'redux-react-session';
import { toast } from 'react-toastify';
import { useLocation } from 'react-router-dom';
import { useUserProfile } from '_clean-code/api/hooks/useUserProfile';
import { useUserRole } from '_clean-code/hooks/useUserRole';

function AppMenu() {
  const location = useLocation();

  const { userData } = useUserProfile();
  const { isAdmin, isSuperAdmin } = useUserRole();

  const isProductionEnabled = useShowProduction();
  const { reportSubscription } = useFeatureFlags();
  const { availability } = useFeatureFlagsAnyEnabled();

  const userEmail = userData?.email;

  const MENU_OPTIONS: Record<string, MenuItem> = useMemo(
    () => ({
      home: {
        id: 'home',
        isNew: false,
        label: <Translate value="dashboard" />,
        to: '/dashboard',
        icon: <LayoutDashboard strokeWidth={1.5} color="#374151" />
      },
      realTime: {
        id: 'real-time',
        label: <Translate value="machines_sidebar" />,
        to: '/machines',
        active: true,
        icon: <AlarmClock strokeWidth={1.5} color="#374151" />
      },
      production: {
        id: 'production',
        label: <Translate value="production_sidebar" />,
        to: '/production',
        active: true,
        icon: <Box strokeWidth={1.5} color="#374151" />
      },
      stops: {
        id: 'stops',
        label: <Translate value="stops" />,
        to: '/stops',
        icon: <CircleAlert strokeWidth={1.5} color="#374151" />
      },
      historical: {
        id: 'analytics',
        isNew: false,
        label: <Translate value="analytics_sidebar" />,
        to: '/analytics',
        icon: <BarChart4 strokeWidth={1.5} color="#374151" />
      },
      reports: {
        id: 'reports',
        label: <Translate value="reports" />,
        to: '/reports',
        icon: <ClipboardList strokeWidth={1.5} color="#374151" />
      },
      export: {
        id: 'export',
        label: <Translate value="data_reports" />,
        to: '/data/reports',
        icon: <ArrowRightFromLine strokeWidth={1.5} color="#374151" />
      },
      settings: {
        id: 'settings',
        label: <Translate value="settings_sidebar" />,
        options: [] as any,
        icon: <Settings strokeWidth={1.5} color="#374151" />
      },
      ...(availability
        ? {
            settings_stopLabels: {
              id: 'stop_labels',
              label: <Translate value="stop_labels" />,
              to: '/stopsettings',
              icon: <></>
            }
          }
        : {}),
      ...(availability
        ? {
            settings_equipment: {
              id: 'equipment',
              label: <Translate value="additional_stop_label" />,
              isNew: false,
              to: '/equipmentsettings',
              icon: <></>
            }
          }
        : {}),
      ...(availability
        ? {
            settings_stopAutomation: {
              id: 'stop_automation',
              label: <Translate value="stop_automation" />,
              isNew: false,
              to: '/stopautomation',
              icon: <></>
            }
          }
        : {}),
      ...(availability
        ? {
            settings_alerts: {
              id: 'alerts_settings_sidebar',
              label: <Translate value="alerts_settings_sidebar" />,
              to: '/alerts',
              isNew: false,
              icon: <></>
            }
          }
        : {}),
      ...(availability
        ? {
            settings_idleShifts: {
              id: 'idleShifts',
              label: <Translate value="idleShifts" />,
              to: '/shift-not-considered',
              icon: <></>
            }
          }
        : {}),
      settings_schedule: {
        id: 'shift_schedule',
        label: <Translate value="shift_schedule" />,
        to: '/shift-settings',
        isNew: false,
        icon: <></>
      },
      settings_machine: {
        id: 'machine_settings',
        label: <Translate value="machine_settings" />,
        isNew: false,
        to: '/machine-settings',
        icon: <></>
      },
      settings_user: {
        id: 'users_settings',
        label: <Translate value="users_settings" />,
        isNew: false,
        to: '/users-settings',
        icon: <></>
      },
      settings_production: {
        id: 'production-settings',
        isNew: true,
        label: <Translate value="production" />,
        to: isProductionEnabled
          ? '/production-settings/sku'
          : '/production-settings/nominal-speed',
        icon: <></>
      },
      tv_settings: {
        id: 'tv_settings',
        label: <Translate value="ui_config" />,
        isNew: true,
        to: '/ui-config/tv',
        icon: <></>
      },
      help: {
        id: 'help',
        label: <Translate value="support_center" />,
        to: '/create-ticket',
        icon: <Headphones strokeWidth={1.5} color="#374151" />
      },
      helpCenter: {
        id: 'help-center',
        label: <Translate value="help_and_docs" />,
        externalLink: true,
        to: 'https://pulsarml.zendesk.com/hc/es',
        icon: <CircleHelp strokeWidth={1.5} color="#374151" />,
        onClick: () => {
          if (!userEmail?.endsWith('pulsarml.com')) {
            window.analytics.page('/help-center', {
              traits: {
                version: 2.2
              }
            });
          }
        }
      }
    }),
    [availability, isProductionEnabled, userEmail]
  );

  useEffect(() => {
    Sentry.configureScope((scope) => scope.setUser(null));
    if (userData?.companies) {
      const {
        email,
        role,
        id: userId,
        timezone,
        companies,
        cognitoUsername
      } = userData;
      Sentry.setUser({ email });
      if (!email?.endsWith('pulsarml.com')) {
        window.analytics.identify(cognitoUsername, {
          traits: {
            version: 2.2,
            userProps: {
              id: userId,
              email: email,
              role: role,
              timezone: timezone
            },
            companyProps: {
              id: companies[0].id,
              name: companies[0].name,
              timezone: companies[0].timezone,
              salesPlan: companies[0].salesPlan
            }
          }
        });
      }
      if (
        location.pathname !== '/equipmentsettings' &&
        location.pathname !== '/stopsettings' &&
        !email?.endsWith('pulsarml.com')
      ) {
        window.analytics.page(location.pathname, {
          traits: {
            version: 2.2,
            userProps: {
              id: userId,
              email: email,
              role: role,
              timezone: timezone
            },
            companyProps: {
              id: companies[0].id,
              name: companies[0].name,
              timezone: companies[0].timezone,
              salesPlan: companies[0].salesPlan
            }
          }
        });
      }
    }
  }, [userData, location, isProductionEnabled]);

  const handleLogout = async () => {
    sessionService.deleteUser();
    sessionService.deleteSession();
    window.localStorage.clear();
    window.location.href = '/login';
    try {
      await Auth.signOut();
    } catch (error) {
      let errorMessage = 'Failed to sign out';
      if (error instanceof Error) {
        errorMessage = error.message;
      }
      toast.error(errorMessage);
    }
  };

  const menuData = useMemo(() => {
    const menuOptions: MenuItem[] = [
      MENU_OPTIONS.home,
      MENU_OPTIONS.realTime,
      ...(availability ? [MENU_OPTIONS.stops] : []),
      MENU_OPTIONS.historical,
      ...(reportSubscription ? [MENU_OPTIONS.reports] : []),
      MENU_OPTIONS.export
    ];
    if (isProductionEnabled) {
      menuOptions.splice(2, 0, MENU_OPTIONS.production);
    }
    const settingsOption = { ...MENU_OPTIONS.settings };
    settingsOption.options = [MENU_OPTIONS.tv_settings];

    if (userEmail && isAdmin) {
      settingsOption.options = [
        ...(availability
          ? [
              MENU_OPTIONS.settings_stopLabels,
              MENU_OPTIONS.settings_equipment,
              MENU_OPTIONS.settings_stopAutomation,
              MENU_OPTIONS.settings_alerts,
              MENU_OPTIONS.settings_idleShifts
            ]
          : []),
        MENU_OPTIONS.settings_schedule,
        MENU_OPTIONS.tv_settings,
        MENU_OPTIONS.settings_machine
      ];

      if (isProductionEnabled) {
        settingsOption.options.splice(3, 0, MENU_OPTIONS.settings_production);
      }

      if (isSuperAdmin) {
        settingsOption.options.push(MENU_OPTIONS.settings_user);
      }
    }

    menuOptions.push(settingsOption);

    return {
      mainOptions: menuOptions,
      footerOptions: [
        MENU_OPTIONS.help,
        MENU_OPTIONS.helpCenter,
        {
          id: 'user',
          label: (
            <div className={'tw-font-sans'}>
              <div className="tw-text-sm">
                <Translate value="profile" />
              </div>
              <div
                className={'tw-cursor-pointer tw-absolute tw-right-1 tw-top-5'}
                onClick={handleLogout}
                title="Logout"
                id="logout-btn"
              >
                <LogOut size={18} color={'#374151'} />
              </div>
              {userEmail && userEmail.length > 10 ? (
                <div
                  title={userEmail}
                  className="tw-text-sm tw-text-label"
                >{`${userEmail.substr(0, 15)}..`}</div>
              ) : (
                <div>{userEmail}</div>
              )}
            </div>
          ),
          icon: (
            <div className="tw-w-6 tw-h-6">
              <CircleUserRound strokeWidth={1.5} color="#374151" />
            </div>
          )
        }
      ]
    };
  }, [
    MENU_OPTIONS,
    availability,
    reportSubscription,
    isProductionEnabled,
    userEmail,
    isAdmin,
    isSuperAdmin
  ]);

  const selectedOption = Object.values(MENU_OPTIONS).find(
    (item) => item.to === location.pathname
  );

  return (
    <PulsarMenuBar
      options={menuData.mainOptions}
      footerOptions={menuData.footerOptions}
      selectedOptionId={selectedOption?.id}
      isNewText={I18n.t('new')}
    />
  );
}

export default AppMenu;
